import { Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'
import { catchError, EMPTY, filter, interval, switchMap, take } from 'rxjs'
import { SystemStatusService } from './system-status.service'

// longer than the 15 s backend cache so users are never sent back on close and at most once on reopen
const POLL_INTERVAL_MS = 30_000

@Component({
  selector: 'app-maintenance',
  styleUrls: ['./maintenance.component.scss'],
  templateUrl: './maintenance.component.html',
})
export class MaintenanceComponent {
  private readonly systemStatus = inject(SystemStatusService)
  private readonly returnUrl = internalUrl(inject(ActivatedRoute).snapshot.queryParamMap.get('returnUrl'))

  constructor() {
    interval(POLL_INTERVAL_MS)
      .pipe(
        // an api unreachable mid-deploy is no reason to reopen
        switchMap(() => this.systemStatus.getStatus().pipe(catchError(() => EMPTY))),
        filter(({ status }) => status === 'ONLINE'),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe(() => this.leave(this.returnUrl))
  }

  // full page reload instead of a router navigation so the user gets the new frontend
  protected leave(url: string): void {
    globalThis.location.assign(url)
  }
}

// keeps a url on this origin unless it is the maintenance page and falls back to the start page otherwise
const internalUrl = (returnUrl: string | null): string => {
  const origin = globalThis.location.origin
  try {
    const url = new URL(returnUrl ?? '/', origin)
    if (url.origin === origin && !url.pathname.startsWith('/maintenance')) {
      // the absolute href keeps the origin while a normalized path like //evil.com would leave it
      return url.href
    }
  } catch {
    // not parseable as a url at all
  }
  return `${origin}/`
}
